import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataTable } from '../../../shared/components';
import { useAuditLogsManagement } from '../../../shared/hooks';
import AuditLogsFilters from './AuditLogsFilters';
import { getAuditLogType, getStatusLabel } from './auditLogTypes';

/**
 * `createdAtFormatted` viene renderizado por Postgres como hora de Buenos Aires
 * ("24/08/2026 14:30:05"). No se construye un Date de JS a propósito: hacerlo
 * reintroduciría el corrimiento de zona horaria que este campo evita.
 */
const splitDateTime = (log) => {
  const value = log?.createdAtFormatted;
  if (!value || typeof value !== 'string') return { date: '—', time: '' };
  const [date, time] = value.split(' ');
  return { date: date || value, time: time || '' };
};

const AuditLogsList = () => {
  const {
    logs,
    loading,
    error,
    filters,
    pagination,
    fetchLogs,
    applyFilters,
    applyFiltersNow,
    seedFilters,
    changePage,
    clearFilters
  } = useAuditLogsManagement();

  const [searchParams] = useSearchParams();
  const seededRef = useRef(false);

  // Cargar una sola vez, sembrando el filtro si venimos del deep-link de Alumnos
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;

    const studentId = searchParams.get('studentId');
    const studentName = searchParams.get('studentName');

    if (studentId) {
      seedFilters({ studentId, studentName: studentName || '' });
      applyFiltersNow({ studentId });
    } else {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      key: 'createdAt',
      label: 'Fecha y hora',
      render: (log) => {
        const { date, time } = splitDateTime(log);
        return (
          <div className="whitespace-nowrap">
            <div className="text-sm font-medium text-gray-900">{date}</div>
            <div className="text-xs font-mono tabular-nums text-gray-500">{time}</div>
          </div>
        );
      }
    },
    {
      key: 'auditLogType',
      label: 'Movimiento',
      render: (log) => {
        const type = getAuditLogType(log.auditLogType);
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${type.badge}`}
          >
            {type.label}
          </span>
        );
      }
    },
    {
      key: 'student',
      label: 'Alumno',
      render: (log) => (
        <div className="text-sm text-gray-900">
          {log.reservation?.student?.name || log.metadata?.studentName || '—'}
        </div>
      )
    },
    {
      key: 'event',
      label: 'Evento',
      render: (log) => (
        <div className="text-sm text-gray-700">
          {log.reservation?.event?.title || log.metadata?.eventTitle || '—'}
        </div>
      )
    },
    {
      key: 'seat',
      label: 'Asiento',
      render: (log) => (
        <div className="text-sm font-mono text-gray-700">
          {log.reservation?.compositeId || log.metadata?.compositeId || '—'}
        </div>
      )
    },
    {
      key: 'transition',
      label: 'Estado',
      render: (log) => {
        const from = getStatusLabel(log.metadata?.oldStatus);
        const to = getStatusLabel(log.metadata?.newStatus);
        if (!from && !to) return <span className="text-gray-400">—</span>;
        return (
          <div className="text-xs text-gray-600 whitespace-nowrap">
            {from || '—'} <span className="text-gray-400">→</span> {to || '—'}
          </div>
        );
      }
    },
    {
      key: 'actor',
      label: 'Realizado por',
      render: (log) => {
        const actor = log.metadata?.actor;
        const name =
          log.user?.name ||
          log.metadata?.actorLabel ||
          (actor === 'student' ? 'Alumno' : actor === 'system' ? 'Sistema' : '—');
        return (
          <div>
            <div className="text-sm text-gray-900">{name}</div>
            {log.userSede && (
              <div className="text-xs text-gray-500">{log.userSede}</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'description',
      label: 'Detalle',
      render: (log) => (
        <div className="text-sm text-gray-600 max-w-xs truncate" title={log.description}>
          {log.description}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Historial de Movimientos
        </h1>
        <p className="text-gray-600 mt-1">
          Todos los movimientos de entradas, ordenados por fecha y hora exacta
        </p>
      </div>

      <AuditLogsFilters
        filters={filters}
        onFiltersChange={applyFilters}
        onClearFilters={clearFilters}
        onSearch={() => applyFiltersNow()}
        loading={loading}
      />

      <div className="bg-white rounded-lg shadow">
        <DataTable
          section="Movimientos"
          data={logs}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={fetchLogs}
          emptyMessage="No se encontraron movimientos para los filtros seleccionados"
          showSearch={false}
          showFilters={false}
          pagination={pagination}
          onPageChange={changePage}
        />
      </div>
    </div>
  );
};

export default AuditLogsList;
